import {
    ButtonSecondary,
    FOCUS_RING_NO_RADIUS,
    Icon,
    RadioGroup,
    type RadioGroupOption,
} from '@amelio/ui-library';
import { useId, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { NO_PILLAR_VALUE, NoPillarChoiceRow, PillarChoiceRow } from './PillarChoiceRows';
import { useFormErrorMessage } from '../../hooks/useFormErrorMessage';
import { ObjectiveLoadErrorMessage, ObjectiveSectionLoader } from '../ObjectivePageStates';

import type { TranslationKey } from '@/i18n-key';
import type { PillarChoice, PillarSelectionMode } from '@/pages/Objectives/hooks/usePillarChoice';
import type { ObjectiveFormData } from '@/pages/Objectives/validation';
import type { Pillar } from '@/types/pillars';
import type { Control } from 'react-hook-form';

import objectivesAlignmentBadge from '@/assets/illustrations/objectivesAlignmentBadge.png';
import AppLink from '@/components/AppLink/AppLink';
import { ALIGNMENT_CARD_TITLE } from '@/components/objectives/objectiveStyles';
import { Permission } from '@/constants/Permission';
import { useUserPermissions } from '@/hooks/api/useUserPermissions';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { RouteName } from '@/navigation/types';
import { offersNoPillarChoice } from '@/pages/Objectives/hooks/usePillarChoice';
import { getLocalizedText } from '@/utils/localization';

/** The radio value while nothing is chosen yet: it matches no row. */
const UNCHOSEN_VALUE = '';

const toRadioValue = (pillarIds: string[] | null): string => {
    if (pillarIds === null) {
        return UNCHOSEN_VALUE;
    }

    return pillarIds[0] ?? NO_PILLAR_VALUE;
};

const SELECT_HELP_KEY: Record<PillarSelectionMode, TranslationKey> = {
    single: 'objectives.form.pillarSelectHelp',
    multiple: 'objectives.form.pillarSelectHelpMultiple',
    removeOnly: 'objectives.form.pillarSelectHelpRemoveOnly',
};

interface PillarAlignmentCardProps {
    /** `beside-vision` keeps the prototype's fixed 675px column next to the vision card;
     * `full-width` is the layout when there is no vision card to sit beside. */
    layout?: 'beside-vision' | 'full-width';
    pillarChoice: PillarChoice;
    control: Control<ObjectiveFormData>;
}

/**
 * "Alignement stratégique": the right-hand card of the form's opening row, holding the strategic
 * pillars the objective will contribute to -- one at most, or several when the company allows it.
 * An objective may be aligned with none.
 */
export default function PillarAlignmentCard({
    pillarChoice,
    control,
    layout = 'beside-vision',
}: PillarAlignmentCardProps) {
    const { t } = useTranslation();
    const translateError = useFormErrorMessage();
    const navigate = useNavigate();
    const { can, isLoading: isLoadingPermissions } = useUserPermissions();
    const titleId = useId();
    // Which pillar rows currently show their (optional, localized) description — purely local
    // UI state, not part of the submitted form.
    const [expandedPillarIds, setExpandedPillarIds] = useState<string[]>([]);

    const togglePillarDescription = (pillarId: string) =>
        setExpandedPillarIds((previous) =>
            previous.includes(pillarId)
                ? previous.filter((id) => id !== pillarId)
                : [...previous, pillarId],
        );

    const renderChoices = () => {
        // The empty state offers a shortcut to whoever manages the pillars: it waits for that
        // permission rather than showing the shortcut late.
        const isEmptyPendingPermission =
            pillarChoice.state === 'ready' &&
            pillarChoice.pillars.length === 0 &&
            isLoadingPermissions;

        if (pillarChoice.state === 'pending' || isEmptyPendingPermission) {
            return <ObjectiveSectionLoader dataTestId={'objective-form-pillar-loader'} />;
        }

        if (pillarChoice.state === 'failed') {
            return <ObjectiveLoadErrorMessage message={t('objectives.form.pillarsLoadError')} />;
        }

        const { mode, pillars } = pillarChoice;

        if (pillars.length === 0) {
            return (
                <div
                    className={'flex flex-col items-start gap-3'}
                    data-testid={'objective-form-pillar-empty'}
                >
                    <p className={'text-navy/80 text-[14px]'}>
                        {t('objectives.form.noPillarOffered')}
                    </p>

                    {/* Publishing is refused with nothing here to pick: say why, not only that the
                        form is invalid. */}
                    {pillarChoice.isPillarRequired && (
                        <p
                            role={'alert'}
                            className={'text-error text-[14px]'}
                        >
                            {t('objectives.form.pillarRequiredNoneActive')}
                        </p>
                    )}

                    {can(Permission.ManageCompanyValues) && (
                        <ButtonSecondary
                            onClick={() => navigate({ path: RouteName.VisionValues })}
                            size={'sm'}
                            fitContent
                        >
                            {t('objectives.pillars.defineInVisionValues')}
                        </ButtonSecondary>
                    )}
                </div>
            );
        }

        return (
            <>
                <p className={'text-navy/80 -mt-1 text-[14px]'}>{t(SELECT_HELP_KEY[mode])}</p>

                <Controller
                    name={'pillarIds'}
                    control={control}
                    render={({ field, fieldState }) => {
                        const pillarRowProps = (pillar: Pillar) => ({
                            name: field.name,
                            pillar,
                            label: getLocalizedText(pillar.name),
                            isExpanded: expandedPillarIds.includes(pillar.id),
                            onToggleDescription: () => togglePillarDescription(pillar.id),
                        });

                        const errorMessage = translateError(fieldState.error?.message);

                        if (mode !== 'single') {
                            const checkedIds = field.value ?? [];

                            return (
                                <div
                                    role={'group'}
                                    aria-labelledby={titleId}
                                    className={'flex w-full flex-col gap-3'}
                                    data-testid={'objective-form-pillar'}
                                >
                                    {pillars.map((pillar) => {
                                        const isSelected = checkedIds.includes(pillar.id);

                                        return (
                                            <PillarChoiceRow
                                                key={pillar.id}
                                                {...pillarRowProps(pillar)}
                                                inputType={'checkbox'}
                                                isSelected={isSelected}
                                                onChange={() =>
                                                    field.onChange(
                                                        isSelected
                                                            ? checkedIds.filter(
                                                                  (id) => id !== pillar.id,
                                                              )
                                                            : [...checkedIds, pillar.id],
                                                    )
                                                }
                                            />
                                        );
                                    })}

                                    {errorMessage && (
                                        <p
                                            role={'alert'}
                                            className={'text-error text-[14px]'}
                                        >
                                            {errorMessage}
                                        </p>
                                    )}
                                </div>
                            );
                        }

                        const pillarsById = new Map(pillars.map((pillar) => [pillar.id, pillar]));
                        const options: RadioGroupOption[] = [
                            ...pillars.map((pillar) => ({
                                label: getLocalizedText(pillar.name),
                                value: pillar.id,
                            })),
                            ...(offersNoPillarChoice(pillarChoice)
                                ? [{ label: t('objectives.form.noPillar'), value: NO_PILLAR_VALUE }]
                                : []),
                        ];

                        return (
                            // The library's `RadioGroup` renders an unnamed `fieldset`, so the
                            // group takes the card title as its name from here.
                            <div
                                role={'radiogroup'}
                                aria-labelledby={titleId}
                                className={'w-full'}
                                data-testid={'objective-form-pillar'}
                            >
                                <RadioGroup
                                    // Its `spacing` presets are gap-2, gap-4 and gap-6; the rows
                                    // sit gap-3 apart.
                                    className={'[&>div]:gap-3'}
                                    name={field.name}
                                    error={!!fieldState.error}
                                    errorMessage={errorMessage}
                                    options={options}
                                    value={toRadioValue(field.value)}
                                    onChange={(value) =>
                                        field.onChange(value === NO_PILLAR_VALUE ? [] : [value])
                                    }
                                    renderOption={({ option, isChecked, onSelect }) => {
                                        if (option.value === NO_PILLAR_VALUE) {
                                            return (
                                                <NoPillarChoiceRow
                                                    name={field.name}
                                                    label={option.label}
                                                    isSelected={isChecked}
                                                    onChange={onSelect}
                                                />
                                            );
                                        }

                                        const pillar = pillarsById.get(option.value);

                                        return (
                                            pillar && (
                                                <PillarChoiceRow
                                                    {...pillarRowProps(pillar)}
                                                    inputType={'radio'}
                                                    isSelected={isChecked}
                                                    onChange={onSelect}
                                                />
                                            )
                                        );
                                    }}
                                />
                            </div>
                        );
                    }}
                />
            </>
        );
    };

    // With no pillar yet, the empty state's own button is the one way to Vision and values; the
    // header link only comes back once there is a pillar to manage (Maxime, 2026-09-14).
    const hasPillars = pillarChoice.state === 'ready' && pillarChoice.pillars.length > 0;

    return (
        <div className={layout === 'full-width' ? 'w-full' : 'obj-alignment-card'}>
            <div
                className={
                    'rounded-[12px] border border-[rgba(238,251,255,1)] bg-white/45 p-[12px_58px_26px_43px] backdrop-blur-[10px]'
                }
            >
                <div className={'flex flex-col gap-2'}>
                    {/* Both levels wrap: on a narrow card the title drops under the badge and
                        "Manage" under the title, instead of pushing past the card's edge. */}
                    <div className={'flex flex-wrap items-center gap-4'}>
                        {/* Intrinsic size of the PNG itself (218x248): height fixed, width left
                            at its natural ratio so the badge is never squeezed narrower. */}
                        <img
                            src={objectivesAlignmentBadge}
                            alt={''}
                            aria-hidden
                            width={218}
                            height={248}
                            className={'h-[156px] w-auto flex-shrink-0'}
                        />

                        <div className={'flex flex-1 flex-wrap items-center gap-x-4 gap-y-2'}>
                            <h3
                                id={titleId}
                                className={ALIGNMENT_CARD_TITLE}
                            >
                                {t('objectives.form.strategicAlignment')}
                            </h3>

                            {/* Only for those who can edit the pillars: anyone else would land on
                                a page they are not allowed to open. */}
                            {can(Permission.ManageCompanyValues) && hasPillars && (
                                <AppLink
                                    to={RouteName.VisionValues}
                                    ariaLabel={t('objectives.form.managePillars')}
                                    className={`${FOCUS_RING_NO_RADIUS} text-navy inline-flex h-[32px] flex-shrink-0 items-center gap-1.5 rounded-lg border border-[rgba(238,251,255,1)] bg-white/70 px-3 text-[12px] font-medium hover:bg-white`}
                                    dataTestId={'objective-form-manage-pillars'}
                                >
                                    <Icon
                                        name={'settings-setting-2'}
                                        size={14}
                                        color={'inherit'}
                                        ariaHidden
                                    />
                                    {t('objectives.form.manage')}
                                </AppLink>
                            )}
                        </div>
                    </div>

                    {renderChoices()}
                </div>
            </div>
        </div>
    );
}
